import { useEffect, useState } from 'react';
import { callApi } from './api';

const SESSION_TIME = '13:30～14:30';

function thursdaysInMonth(year, month) {
  const dates = [];
  const d = new Date(year, month - 1, 1);
  while (d.getDay() !== 4) d.setDate(d.getDate() + 1);
  while (d.getMonth() === month - 1) {
    dates.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return dates;
}
function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** ほおっちょけん：100歳体操（毎週木曜）の担当職員を月単位で割り当てる画面。 */
export default function HocchokenScheduleScreen({ onBack }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth() + 1);

  const [staff, setStaff] = useState(null); // null=読み込み中
  const [newStaffName, setNewStaffName] = useState('');
  const [staffBusy, setStaffBusy] = useState(false);

  const [schedule, setSchedule] = useState({}); // dateStr -> {staffIds, isCancelled}（未保存の日は無し＝全員が既定）
  const [locked, setLocked] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingDate, setSavingDate] = useState('');

  useEffect(() => {
    let cancelled = false;
    callApi('hocchoken_staff_list')
      .then((data) => { if (!cancelled) setStaff(data.staff); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    callApi('hocchoken_schedule_get', { year: viewYear, month: viewMonth })
      .then((data) => {
        if (cancelled) return;
        const map = {};
        (data.schedule || []).forEach((row) => {
          map[row.session_date] = { staffIds: row.staff_ids || [], isCancelled: row.is_cancelled };
        });
        setSchedule(map);
        setLocked(Boolean(data.locked));
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [viewYear, viewMonth]);

  function changeMonth(delta) {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setViewYear(y);
    setViewMonth(m);
  }

  // 未保存の日は「全員が候補（何も除外していない）」を既定値にする
  // （＝まず全員表示、行けない人を外していく運用のため）。
  function getEntry(dateStr) {
    const saved = schedule[dateStr];
    if (saved) return saved;
    return { staffIds: (staff || []).map((s) => s.id), isCancelled: false };
  }

  async function updateDay(dateStr, patch) {
    const next = { ...getEntry(dateStr), ...patch };
    setSchedule((s) => ({ ...s, [dateStr]: next }));
    setSavingDate(dateStr);
    setError('');
    try {
      await callApi('hocchoken_schedule_set', { date: dateStr, staffIds: next.staffIds, isCancelled: next.isCancelled });
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingDate('');
    }
  }

  async function handleToggleLock() {
    if (lockBusy) return;
    setLockBusy(true);
    setError('');
    try {
      await callApi('hocchoken_schedule_lock_set', { year: viewYear, month: viewMonth, locked: !locked });
      setLocked((v) => !v);
    } catch (err) {
      setError(err.message);
    } finally {
      setLockBusy(false);
    }
  }

  function toggleStaff(dateStr, staffId) {
    const entry = getEntry(dateStr);
    const staffIds = entry.staffIds.includes(staffId)
      ? entry.staffIds.filter((id) => id !== staffId)
      : [...entry.staffIds, staffId];
    updateDay(dateStr, { staffIds });
  }

  async function handleAddStaff(e) {
    e.preventDefault();
    if (!newStaffName.trim() || staffBusy) return;
    setStaffBusy(true);
    setError('');
    try {
      const data = await callApi('hocchoken_staff_create', { name: newStaffName.trim() });
      setStaff((prev) => [...(prev || []), data.staff]);
      setNewStaffName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setStaffBusy(false);
    }
  }

  async function handleRemoveStaff(id) {
    if (staffBusy) return;
    setStaffBusy(true);
    setError('');
    try {
      await callApi('hocchoken_staff_delete', { staffId: id });
      setStaff((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setStaffBusy(false);
    }
  }

  const thursdays = thursdaysInMonth(viewYear, viewMonth);

  return (
    <div className="workspace">
      <div className="workspace-head reveal r1">
        <button className="back-link" type="button" onClick={onBack}>← ホームへ戻る</button>
        <h1 className="workspace-title">100歳体操 当番表</h1>
      </div>

      <p className="notice reveal r2">毎週木曜　{SESSION_TIME}</p>

      <div className="editor-card reveal r2">
        <h2 className="admin-dept-title">担当職員</h2>
        {staff === null ? (
          <p className="workspace-loading">読み込んでいます…</p>
        ) : (
          <div className="hc-staff-row">
            {staff.map((s) => (
              <span className="hc-staff-chip" key={s.id}>
                {s.name}
                <button type="button" onClick={() => handleRemoveStaff(s.id)} disabled={staffBusy} aria-label={`${s.name}を削除`}>×</button>
              </span>
            ))}
            <form className="hc-staff-add" onSubmit={handleAddStaff}>
              <input
                type="text" placeholder="職員名を追加" value={newStaffName}
                onChange={(e) => setNewStaffName(e.target.value)} disabled={staffBusy}
              />
              <button type="button" className="copy-all-btn" onClick={handleAddStaff} disabled={!newStaffName.trim() || staffBusy}>
                追加
              </button>
            </form>
          </div>
        )}
      </div>

      <div className="editor-card reveal r3">
        <div className="hc-month-nav">
          <button type="button" className="back-link" onClick={() => changeMonth(-1)}>← 前の月</button>
          <h2 className="admin-dept-title">{viewYear}年{viewMonth}月</h2>
          <button type="button" className="back-link" onClick={() => changeMonth(1)}>次の月 →</button>
        </div>
        <div className="hc-lock-row">
          <p className="admin-lock-note">
            {locked
              ? 'この月の当番は決定済みです。変更するには決定を取り消してください。'
              : '全員が候補として表示されます。行けない人は自身の名前を消してください。20日頃に日程を決定します。'}
          </p>
          <button type="button" className="copy-all-btn" disabled={lockBusy || loading} onClick={handleToggleLock}>
            {lockBusy ? '処理中…' : locked ? '決定を取り消す' : 'この月の当番を決定する'}
          </button>
        </div>

        {loading && <p className="workspace-loading">読み込んでいます…</p>}
        {error && <p className="pin-error" role="alert">{error}</p>}

        {!loading && (
          <div className="hc-schedule-list">
            {thursdays.map((d) => {
              const dateStr = toDateStr(d);
              const entry = getEntry(dateStr);
              return (
                <div className={`hc-schedule-row${entry.isCancelled ? ' is-cancelled' : ''}`} key={dateStr}>
                  <div className="hc-schedule-row-top">
                    <span className="hc-schedule-date">{d.getMonth() + 1}月{d.getDate()}日（木）{SESSION_TIME}</span>
                    <button
                      type="button" className="pii-replace-btn"
                      disabled={locked || savingDate === dateStr}
                      onClick={() => updateDay(dateStr, {
                        isCancelled: !entry.isCancelled,
                        staffIds: entry.isCancelled ? entry.staffIds : [],
                      })}
                    >
                      {entry.isCancelled ? '開催に戻す' : 'この回は中止'}
                    </button>
                  </div>
                  {entry.isCancelled ? (
                    <span className="hc-schedule-cancelled-label">中止</span>
                  ) : (
                    <div className="hc-staff-toggle-row">
                      {(staff || []).map((s) => {
                        const included = entry.staffIds.includes(s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            className={`hc-staff-toggle${included ? '' : ' is-excluded'}`}
                            disabled={locked || savingDate === dateStr}
                            onClick={() => toggleStaff(dateStr, s.id)}
                          >
                            {s.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
